import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type PortalPosition = {
    top: number;
    left: number;
    width: number | string;
};

/**
 * Unified dropdown portal management:
 * - Calculates absolute screen position of the trigger element
 * - Handles clicks outside both the trigger and the menu
 * - Handles auto-close on Escape
 * - Repositions on scroll/resize
 */
export const useDropdownPortal = (
    containerRef: RefObject<HTMLElement>,
    open: boolean,
    setOpen: (v: boolean) => void
) => {
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<PortalPosition>({ top: 0, left: 0, width: 'auto' });

    // 🧭 Track menu position
    useLayoutEffect(() => {
        if (!open || !containerRef.current) return;

        const update = () => {
            const rect = containerRef.current!.getBoundingClientRect();
            setPos({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [containerRef, open]);

    // 🖱 Handle clicks + Escape
    useLayoutEffect(() => {
        if (!open) return;

        const handleDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                !containerRef.current?.contains(target) &&
                !menuRef.current?.contains(target)
            ) {
                setOpen(false);
            }
        };

        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handleDocClick);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleDocClick);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [containerRef, open, setOpen]);

    /**
     * Renders dropdown content in a portal, positioned correctly.
     * Usage:
     *    {portal(<div> ...menu... </div>)}
     */
    const portal = (children: React.ReactNode, extraStyle?: React.CSSProperties) =>
        open
            ? createPortal(
                <div
                    ref={menuRef}
                    style={{
                        position: 'absolute',
                        top: pos.top,
                        left: pos.left,
                        width: pos.width,
                        zIndex: 9999,
                        ...extraStyle,
                    }}
                >
                    {children}
                </div>,
                document.body
            )
            : null;

    return { portal, menuRef, pos };
};